import React, { useState, useEffect, useMemo } from "react";
import useRealtimeDraft from '../../../realtime/useRealtimeDraft';
import { useSelector, useDispatch } from "react-redux";
import { selectUserDetails, selectUserRoles } from "../../../redux/slices/userSlice";
import { selectJobChangeOrderDetails, selectJobId, selectChangeOrderParent, selectGeneralProject, selectChangeOrderMailingList } from "../../../redux/selectors";
import { selectApiUrls } from "../../../redux/slices/apiUrlSlice";
import { Container, Row, Col, Form, Button, Alert } from 'react-bootstrap';
import { formatDateForEmail, formatDateForDisplay, formatDateForAPI } from "../../../utils/dateUtils";
import { patchData, postData } from '../../../redux/slices/apiCallSlice';
import { fetchJobDetails } from '../../../redux/slices/fetchJobDetailsSlice';
import useSendEmail from '../../../functions/useSendEmail';
import { Html, Hr, Text, Heading, Section, Column } from "@react-email/components";
import { render } from '@react-email/render';
import { Ol } from "@react-email/ol";
import useUserContact from '../../../functions/useUserContact';

const SendChangeOrder = ({ notes, onClose, impactsCSM, includeAllControls }) => {
    useRealtimeDraft(true); // Keep the reviewed publish snapshot stable until closed.
    const jobId = useSelector(selectJobId);
    const userDetails = useSelector(selectUserDetails);
    const userRoles = useSelector(selectUserRoles);
    const projectDetails = useSelector(selectGeneralProject);
    const changeOrderDetails = useSelector(selectJobChangeOrderDetails); // Use job-specific selector from fullJobFetchSlice
    const changeOrderParent = useSelector(selectChangeOrderParent);
    const changeOrderRecipients = useSelector(selectChangeOrderMailingList) || [];
    const currentUser = userDetails?.name;
        
    const dispatch = useDispatch();

    const sendEmail = useSendEmail();

    const tbdList = useSelector((state) => state.tbdApi.tbdList);  
    const filteredTbdList = useMemo(() => {
        return tbdList.filter(
            (tbdItem) => String(tbdItem.ProjectID) === String(jobId)
        );
    }, [tbdList, jobId]);
    
    const apiUrls = useSelector(selectApiUrls);
    const currentDate = formatDateForDisplay(new Date());
    const [sendToUser, setSendToUser] = useState(userRoles.includes("Admin"));
    const [additionalRecipients, setAdditionalRecipients] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [isRefreshingData, setIsRefreshingData] = useState(false);
    const [localChangeOrderRecipients, setLocalChangeOrderRecipients] = useState(changeOrderRecipients);
    const { userContacts, loading, error } = useUserContact();
    const [missingEngineers, setMissingEngineers] = useState([]);

    const getFirstDefinedValue = (detail, keys) => {
        for (const key of keys) {
            const value = detail?.[key];
            if (value !== undefined && value !== null) {
                return value;
            }
        }
        return undefined;
    };

    const toDisplayValue = (value, fallback = "BLANK") => {
        return value === undefined || value === null || value === "" ? fallback : value;
    };

    const toCamelCaseKey = (key) => key.charAt(0).toLowerCase() + key.slice(1);
    const toSnakeCaseKey = (key) => key.replace(/[A-Z]/g, (letter, index) => `${index > 0 ? "_" : ""}${letter.toLowerCase()}`);

    const getFieldValue = (detail, key) => {
        return getFirstDefinedValue(detail, [key, toCamelCaseKey(key), toSnakeCaseKey(key)]);
    };

    const getDetailTitle = (detail) => toDisplayValue(getFirstDefinedValue(detail, ["Title", "title"]));
    const getDetailField = (detail) => {
        const field = getFirstDefinedValue(detail, ["Field", "field", "FieldName", "fieldName", "field_name"]);
        if (field === undefined || field === null) {
            return "";
        }

        const normalizedField = typeof field === "string" ? field.trim() : field;
        return normalizedField === "" || normalizedField === "BLANK" ? "" : normalizedField;
    };
    const getDetailExistingValue = (detail) => toDisplayValue(getFirstDefinedValue(detail, ["ExistingValue", "existingValue", "PreviousValue", "previousValue", "PrevValue", "prevValue", "existing_value", "previous_value"]));
    const getDetailNewValue = (detail) => toDisplayValue(getFirstDefinedValue(detail, ["NewValue", "newValue", "UpdatedValue", "updatedValue", "new_value", "updated_value"]));
    const getReasonForChange = (detail) => toDisplayValue(getFirstDefinedValue(detail, ["ReasonForChange", "reasonForChange", "reason_for_change"]), "Reason Not Assigned");

    const getChangeLabel = (detail) => {
        const title = getDetailTitle(detail);
        const field = getDetailField(detail);
        return field ? `${title} ${field}` : title;
    };

    const getStatus = (detail) => getFirstDefinedValue(detail, ["Status", "status"]);
    const getBillCreditTo = (detail) => getFirstDefinedValue(detail, ["BillCreditTo", "billCreditTo", "bill_credit_to"]);
    const getChangeOrderNumber = (detail) => getFirstDefinedValue(detail, ["ChangeOrderNumber", "changeOrderNumber", "change_order_number"]);
    const hasChangeOrderNumber = (detail) => {
        const value = getChangeOrderNumber(detail);
        return value !== undefined && value !== null && value !== "";
    };

    const hasActionRequiredMechanical = (detail) => Boolean(getFirstDefinedValue(detail, ["ActionRequiredMechanical", "actionRequiredMechanical", "action_required_mechanical"]));
    const hasActionRequiredElectrical = (detail) => Boolean(getFirstDefinedValue(detail, ["ActionRequiredElectrical", "actionRequiredElectrical", "action_required_electrical"]));
    const hasActionRequiredControls = (detail) => Boolean(getFirstDefinedValue(detail, ["ActionRequiredControls", "actionRequiredControls", "action_required_controls"]));
    const hasActionRequiredCSM = (detail) => Boolean(getFirstDefinedValue(detail, ["ActionRequiredCSM", "actionRequiredCSM", "action_required_csm"]));

    const countChangeOrderParent = () => {
        return changeOrderParent ? changeOrderParent.length : 0;
    };

    // Filter items that do NOT have a change order number key/value (pending items from fullJobFetch API)
    const includeOnChangeOrder = changeOrderDetails?.filter(detail => !hasChangeOrderNumber(detail)) || [];
    
    // Sort previousChanges in descending order by ChangeOrderNumber
    // Only include items that have the ChangeOrderNumber key
    const previousChanges = (changeOrderDetails || [])
        .filter(detail => hasChangeOrderNumber(detail))
        .sort((a, b) => {
            const numA = parseInt(getChangeOrderNumber(a), 10) || 0;
            const numB = parseInt(getChangeOrderNumber(b), 10) || 0;
            return numB - numA;
        });

    // Check if the selected change order has action items
    // Filter items that do NOT have ChangeOrderNumber key (pending items)
    const hasActionItems = (changeOrderDetails || []).some(detail =>
        !hasChangeOrderNumber(detail) &&
        (hasActionRequiredMechanical(detail) || hasActionRequiredElectrical(detail) || hasActionRequiredControls(detail) || hasActionRequiredCSM(detail))
    );

    const noBill = includeOnChangeOrder.filter(detail => {
        const billCreditTo = getBillCreditTo(detail);
        const status = getStatus(detail);
        return (billCreditTo === null || billCreditTo === "N/A" || billCreditTo === "" || billCreditTo === "BLANK" || billCreditTo === undefined) && status !== "Follow Up";
    });
    const watertronicsBill = includeOnChangeOrder.filter(detail => getBillCreditTo(detail) === "Watertronics" && getStatus(detail) !== "Follow Up");
    const customerBill = includeOnChangeOrder.filter(detail => getBillCreditTo(detail) === "Customer" && getStatus(detail) !== "Follow Up");
    const followUp = includeOnChangeOrder.filter(detail => getStatus(detail) === "Follow Up");

    const groupAndSortByReason = (details) => {
        const grouped = details.reduce((acc, detail) => {
            const reason = getReasonForChange(detail);
            if (!acc[reason]) {
                acc[reason] = [];
            }
            acc[reason].push(detail);
            return acc;
        }, {});

        return Object.keys(grouped).sort().map(reason => ({
            reason,
            details: grouped[reason]
        }));
    };

    const groupedNoBill = groupAndSortByReason(noBill);
    const groupedCustomerBill = groupAndSortByReason(customerBill);
    const groupedWatertronicsBill = groupAndSortByReason(watertronicsBill);

    const actionRequiredDepartments = [];
    if ((changeOrderDetails || []).some(detail => hasActionRequiredMechanical(detail))) {
        actionRequiredDepartments.push(`${projectDetails[0]?.MechanicalEngineer || "Mechanical Engineering"} (Mechanical Engineering)`);
    }
    if ((changeOrderDetails || []).some(detail => hasActionRequiredElectrical(detail))) {
        actionRequiredDepartments.push(`${projectDetails[0]?.ElectricalEngineer || "Electrical Engineering"} (Electrical Engineering)`);
    }
    if ((changeOrderDetails || []).some(detail => hasActionRequiredControls(detail))) {
        actionRequiredDepartments.push(`${projectDetails[0]?.ControlsEngineer || "Controls Engineering"} (Controls Engineering)`);
    }
    if ((changeOrderDetails || []).some(detail => hasActionRequiredCSM(detail))) {
        actionRequiredDepartments.push("David Neesen (CSM)");
    }

    const formatDepartments = (departments) => {
        if (departments.length === 2) {
            return departments.join(" & ");
        } else if (departments.length > 2) {
            return departments.slice(0, -1).join(", ") + " & " + departments.slice(-1);
        }
        return departments.join(", ");
    };

    const handleRecipientChange = () => {
        setSendToUser(!sendToUser);
    };

    const handleAdditionalRecipientsChange = (e) => {
        setAdditionalRecipients(e.target.value);
    };

    const calculateTotal = (details, field, costCreditField) => {
        return details.reduce((acc, detail) => {
            // Handle undefined/null values from fullJobFetch API
            const fieldValue = parseFloat(getFieldValue(detail, field)) || 0;
            const costCreditValue = getFieldValue(detail, costCreditField);
            
            // If costCreditField is undefined (missing from API), default to false (cost)
            // costCreditValue === true means credit (positive), anything else means cost (negative)
            const multiplier = (costCreditValue === true || costCreditValue === "true") ? 1 : -1;
            const value = fieldValue * multiplier;
            
            return acc + value;
        }, 0);
    };

    const customerManufacturingLaborTotal = calculateTotal(customerBill, 'ManufacturingLaborHours', 'ManufacturingCostCredit') * 90;
    const customerEngineeringLaborTotal = calculateTotal(customerBill, 'EngineeringLaborHours', 'EngineeringCostCredit') * 165;
    const customerMaterialsVarianceTotal = calculateTotal(customerBill, 'MaterialsVariance', 'MaterialCostCredit');
    const customerScrapTotal = calculateTotal(customerBill, 'Scrap', 'ScrapCostCredit');

    const watertronicsManufacturingLaborTotal = calculateTotal(watertronicsBill, 'ManufacturingLaborHours', 'ManufacturingCostCredit') * 90;
    const watertronicsEngineeringLaborTotal = calculateTotal(watertronicsBill, 'EngineeringLaborHours', 'EngineeringCostCredit') * 165;
    const watertronicsMaterialsVarianceTotal = calculateTotal(watertronicsBill, 'MaterialsVariance', 'MaterialCostCredit');
    const watertronicsScrapTotal = calculateTotal(watertronicsBill, 'Scrap', 'ScrapCostCredit');

    const totalManufacturingLabor = customerManufacturingLaborTotal + watertronicsManufacturingLaborTotal;
    const totalEngineeringLabor = customerEngineeringLaborTotal + watertronicsEngineeringLaborTotal;
    const totalMaterialsVariance = customerMaterialsVarianceTotal + watertronicsMaterialsVarianceTotal;
    const totalScrap = customerScrapTotal + watertronicsScrapTotal;

    const customerBillTotal = customerManufacturingLaborTotal + customerEngineeringLaborTotal + customerMaterialsVarianceTotal + customerScrapTotal;
    const watertronicsBillTotal = watertronicsManufacturingLaborTotal + watertronicsEngineeringLaborTotal + watertronicsMaterialsVarianceTotal + watertronicsScrapTotal;

    const formatCostCredit = (value) => {
        return value < 0 ? `${Math.abs(value).toFixed(2)} Cost` : `${value.toFixed(2)} Credit`;
    };

    const renderHighlightedChangeValue = (existingValue, newValue) => {
        const existingText = String(existingValue ?? "BLANK");
        const newText = String(newValue ?? "BLANK");

        if (!existingText || !newText || existingText === "BLANK" || newText === "BLANK") {
            return newText || "BLANK";
        }

        const maxComparableLength = Math.min(existingText.length, newText.length);

        let commonPrefixLength = 0;
        while (
            commonPrefixLength < maxComparableLength &&
            existingText[commonPrefixLength] === newText[commonPrefixLength]
        ) {
            commonPrefixLength += 1;
        }

        let commonSuffixLength = 0;
        while (
            commonSuffixLength < maxComparableLength - commonPrefixLength &&
            existingText[existingText.length - 1 - commonSuffixLength] === newText[newText.length - 1 - commonSuffixLength]
        ) {
            commonSuffixLength += 1;
        }

        const hasOverlap = commonPrefixLength > 0 || commonSuffixLength > 0;
        if (!hasOverlap) {
            return newText;
        }

        const changedStart = commonPrefixLength;
        const changedEnd = newText.length - commonSuffixLength;
        const changedSegment = newText.slice(changedStart, changedEnd);

        if (!changedSegment) {
            return newText;
        }

        const prefix = newText.slice(0, changedStart);
        const suffix = newText.slice(changedEnd);

        return (
            <>
                {prefix}
                <span style={{ backgroundColor: '#fff59d', padding: '0 2px' }}>{changedSegment}</span>
                {suffix}
            </>
        );
    };

    const renderChangeDescription = (detail) => {
        const label = getChangeLabel(detail);
        const existingValue = getDetailExistingValue(detail);
        const newValue = getDetailNewValue(detail);

        return (
            <>
                {label} <span style={{ color: 'red' }}>changed from</span> {existingValue} <span style={{ color: 'red' }}>to</span> {renderHighlightedChangeValue(existingValue, newValue)}
            </>
        );
    };

    useEffect(() => {
        if (!loading && !error && userContacts && typeof userContacts === "object") {
            // Compare project details with userContacts and add matching emails to the recipient list
            const matchedRecipients = [];
            const missingEngineersList = [];

            const rolesToCheck = [
                { role: "SalesRep", label: "Sales Representative", key: "sales" },
                { role: "ProjectManager", label: "Project Manager", key: "projectManager" },
                { role: "MechanicalEngineer", label: "Mechanical Engineer", key: "mechanicalEngineer" },
                { role: "ElectricalEngineer", label: "Electrical Engineer", key: "electricalEngineer" },
                { role: "ControlsEngineer", label: "Controls Engineer", key: "controlsEngineer" }
            ];

            rolesToCheck.forEach(({ role, key, label }) => {
                const roleName = projectDetails[0]?.[role];
                if (roleName && roleName !== "BLANK" && roleName !== "") {
                    const contactList = userContacts[key] || [];
                    const matchedContact = contactList.find(contact => contact.name === roleName);
                    if (matchedContact) {
                        matchedRecipients.push({
                            displayName: matchedContact.name,
                            email: matchedContact.email
                        });
                    } else if (role.includes("Engineer")) {
                        // Only track missing engineers
                        missingEngineersList.push({ role: label, name: roleName });
                    }
                }
            });

            // Set missing engineers
            setMissingEngineers(missingEngineersList);

            // Add all ControlsEngineer users if includeAllControls is true
            if (includeAllControls) {
                const controlsContacts = userContacts.controlsEngineer || [];
                controlsContacts.forEach(contact => {
                    if (!matchedRecipients.some(recipient => recipient.email === contact.email)) {
                        matchedRecipients.push({
                            displayName: contact.name,
                            email: contact.email
                        });
                    }
                });
            }

            // Combine matched recipients with existing recipients without causing re-render loop
            setLocalChangeOrderRecipients(prevRecipients => {
                const updatedRecipients = [
                    ...prevRecipients,
                    ...matchedRecipients.filter(
                        newRecipient =>
                            !prevRecipients.some(
                                existingRecipient => existingRecipient.email === newRecipient.email
                            )
                    )
                ];
                
                // Only update state if the array has actually changed
                if (JSON.stringify(prevRecipients) !== JSON.stringify(updatedRecipients)) {
                    return updatedRecipients;
                }
                return prevRecipients;
            });
        }
    }, [userContacts, loading, error, projectDetails, includeAllControls]);
    

    const handleSubmitChangeOrder = async () => {
        // Clear any previous errors and start the submission process
        setErrorMessage("");
        setIsSubmitting(true);
        
        try {
            // Step 1: Refresh change order parent data to get the latest count and reduce race conditions
            setIsRefreshingData(true);
            console.log('Refreshing change order parent data before submission...');
            
            await dispatch(fetchJobDetails({
                jobId,
                apiUrls: {
                    changeOrderParent: apiUrls.changeOrderParent
                }
            })).unwrap();
            
            setIsRefreshingData(false);
            
            // Step 2: Calculate the next change order number with fresh data
            const nextChangeOrderNumber = countChangeOrderParent() + 1;
            console.log(`Creating change order #${nextChangeOrderNumber} for job ${jobId}`);
            
            // Step 3: Create the change order parent record
            const changeOrderParentData = {
                ID: null,
                ProjectID: jobId,
                JobName: projectDetails[0]?.ProjectName || "BLANK",
                JobNumber: projectDetails[0]?.JobNumber || "BLANK", 
                ChangeOrderNumber: nextChangeOrderNumber,
                DateSubmitted: formatDateForAPI(new Date()),
                WrittenBy: currentUser || "BLANK",
                DoChangesImpactCSM: impactsCSM,
                SalesPerson: projectDetails[0]?.SalesRep || "BLANK",
                AppsEngineer: projectDetails[0]?.ProjectManager || "BLANK",
                MechanicalEngineer: projectDetails[0]?.MechanicalEngineer || "BLANK",
                ElectricalEngineer: projectDetails[0]?.ElectricalEngineer || "BLANK",
                IncludeControlEngineer: includeAllControls,
                Notes: notes || "BLANK"
            };
            
            console.log('Creating change order parent record...');
            const parentResult = await dispatch(postData({ 
                url: apiUrls.changeOrderParent, 
                data: changeOrderParentData 
            })).unwrap();
            
            console.log('Change order parent record created successfully:', parentResult);
            
            // Debug: Log the raw details before mapping
            console.log('Raw noBill details:', noBill);
            console.log('Raw watertronicsBill details:', watertronicsBill);
            console.log('Raw customerBill details:', customerBill);
            console.log('Raw followUp details:', followUp);
            
            // Step 4: Update change order details with the change order number
            // Include all items: noBill, watertronicsBill, customerBill, AND followUp items
            // Normalize all fields to PascalCase for API consistency
            const updateDetails = [...noBill, ...watertronicsBill, ...customerBill, ...followUp].map(detail => {
                // Debug: Log each detail's ID fields
                console.log('Detail ID fields:', { 
                    ID: detail.ID, 
                    id: detail.id,
                    DetailID: detail.DetailID,
                    detailID: detail.detailID,
                    ChangeOrderDetailID: detail.ChangeOrderDetailID
                });
                
                return {
                    ID: detail.ID ?? detail.id ?? detail.DetailID ?? detail.detailID,
                    ProjectID: detail.ProjectID ?? detail.projectID ?? jobId,
                    Title: detail.Title ?? detail.title,
                    Field: detail.Field ?? detail.field,
                    ExistingValue: detail.ExistingValue ?? detail.existingValue,
                    NewValue: detail.NewValue ?? detail.newValue,
                    ReasonForChange: detail.ReasonForChange ?? detail.reasonForChange,
                    BillCreditTo: detail.BillCreditTo ?? detail.billCreditTo,
                    ActionRequiredMechanical: detail.ActionRequiredMechanical ?? detail.actionRequiredMechanical,
                    ActionRequiredElectrical: detail.ActionRequiredElectrical ?? detail.actionRequiredElectrical,
                    ActionRequiredControls: detail.ActionRequiredControls ?? detail.actionRequiredControls,
                    ActionRequiredCSM: detail.ActionRequiredCSM ?? detail.actionRequiredCSM,
                    FollowUpClosed: detail.FollowUpClosed ?? detail.followUpClosed,
                    ChangeOrderNumber: String(nextChangeOrderNumber),
                    Status: "Resolved"
                };
            });
            
            console.log('Change order details to update:', updateDetails);
            
            if (updateDetails.length > 0) {
                console.log(`Updating ${updateDetails.length} change order detail records...`);
                
                // PATCH each detail individually - API doesn't support bulk updates
                for (const detail of updateDetails) {
                    await dispatch(patchData({ 
                        url: apiUrls.changeOrderDetails, 
                        data: detail 
                    })).unwrap();
                }
                
                console.log('Change order details updated successfully');
            }
        
            // Step 5: Generate email content
            console.log('Generating email content...');
            const emailBody = await render(
                <Html>
                    <Section>
                        <Row>
                            <Column align="center">
                                <Heading as="h1"><strong>Change Order # {nextChangeOrderNumber}</strong></Heading>
                                <Heading as="h2">{projectDetails[0]?.JobNumber || "BLANK"} - {projectDetails[0]?.ProjectName || "BLANK"}</Heading>
                                <Heading as="h4">{projectDetails[0]?.ModelNumber || "BLANK"}</Heading>
                                {projectDetails[0]?.ProjectManager === currentUser ? (
                                    <Text><strong>Written By:</strong> {currentUser || "BLANK"} on {formatDateForEmail(new Date())}. <strong>Sales Rep:</strong> {projectDetails[0]?.SalesRep || "BLANK"}</Text>
                                ) : (
                                    <Text><strong>Written By:</strong> {currentUser || "BLANK"} on behalf of {projectDetails[0]?.ProjectManager || "BLANK"} on {formatDateForEmail(new Date())}. <strong>Sales Rep:</strong> {projectDetails[0]?.SalesRep || "BLANK"}</Text>
                                )}
                                <Text>
                                  {projectDetails[0]?.MechanicalEngineer && projectDetails[0]?.MechanicalEngineer !== "BLANK" && `\ud83d\udd27 ${projectDetails[0]?.MechanicalEngineer}`}
                                  {projectDetails[0]?.MechanicalEngineer && projectDetails[0]?.MechanicalEngineer !== "BLANK" && projectDetails[0]?.ElectricalEngineer && projectDetails[0]?.ElectricalEngineer !== "BLANK" && " | "}
                                  {projectDetails[0]?.ElectricalEngineer && projectDetails[0]?.ElectricalEngineer !== "BLANK" && `\ud83d\udd0c ${projectDetails[0]?.ElectricalEngineer}`}
                                  {projectDetails[0]?.ElectricalEngineer && projectDetails[0]?.ElectricalEngineer !== "BLANK" && projectDetails[0]?.ControlsEngineer && projectDetails[0]?.ControlsEngineer !== "BLANK" && " | "}
                                  {projectDetails[0]?.ControlsEngineer && projectDetails[0]?.ControlsEngineer !== "BLANK" && `\ud83d\udda7 ${projectDetails[0]?.ControlsEngineer}`}
                                </Text>
                            </Column>
                        </Row>
                    </Section>
                    <Hr />
                    {actionRequiredDepartments.length > 0 && (
                        <>
                            <Section>
                                <Text><span style={{ color: 'red', fontWeight: 'bold' }}>This change order includes assigned action items for {formatDepartments(actionRequiredDepartments)}. Please review Change Order Tasks in the Production Portal for more information.</span></Text>
                            </Section>
                            <Hr />
                        </>
                    )}
                    {groupedNoBill.length > 0 && (
                        <>
                            <Section>
                                <Heading as="h2">Changes</Heading>
                                {groupedNoBill.map((group, groupIndex) => (
                                    <div key={groupIndex}>
                                        <Heading as="h4">{group.reason || "BLANK"}:</Heading>
                                        <Ol>
                                            {group.details.map((detail, detailIndex) => (
                                                <li key={detailIndex}>
                                                    <Text>{renderChangeDescription(detail)}</Text>
                                                </li>
                                            ))}
                                        </Ol>
                                    </div>
                                ))}
                            </Section>
                            <Hr />
                        </>
                    )}
                    {groupedCustomerBill.length > 0 && (
                        <>
                            <Section>
                                <Heading as="h2">Changes Billed to Customer</Heading>
                                {groupedCustomerBill.map((group, groupIndex) => (
                                    <div key={groupIndex}>
                                        <Heading as="h4">{group.reason || "BLANK"}:</Heading>
                                        <Ol>
                                            {group.details.map((detail, detailIndex) => (
                                                <li key={detailIndex}>
                                                    <Text>{renderChangeDescription(detail)}</Text>
                                                </li>
                                            ))}
                                        </Ol>
                                    </div>
                                ))}
                            </Section>
                            <Hr />
                        </>
                    )}
                    {groupedWatertronicsBill.length > 0 && (
                        <>
                            <Section>
                                <Heading as="h2">Changes Billed to Watertronics</Heading>
                                {groupedWatertronicsBill.map((group, groupIndex) => (
                                    <div key={groupIndex}>
                                        <Heading as="h4">{group.reason || "BLANK"}:</Heading>
                                        <Ol>
                                            {group.details.map((detail, detailIndex) => (
                                                <li key={detailIndex}>
                                                    <Text>{renderChangeDescription(detail)}</Text>
                                                </li>
                                            ))}
                                        </Ol>
                                    </div>
                                ))}
                            </Section>
                            <Hr />
                        </>
                    )}
                    {followUp.length > 0 && (
                        <>
                            <Section>
                                <Heading as="h2">Follow Up</Heading>
                                <Ol>
                                    {followUp.map((detail, index) => (
                                        <li key={index}>
                                            <Text>{renderChangeDescription(detail)}</Text>
                                        </li>
                                    ))}
                                </Ol>
                            </Section>
                            <Hr />
                        </>
                    )}
                    {filteredTbdList.length > 0 && (
                        <>
                            <Section>
                                <Heading as="h2">TBD List</Heading>
                                <Ol>
                                    {filteredTbdList.map((tbdItem, index) => (
                                        <li key={index}>
                                            <Text><strong>{tbdItem.ColumnName || "BLANK"} ({tbdItem.Table || "BLANK"}):</strong> {tbdItem.Value || "BLANK"}</Text>
                                        </li>
                                    ))}
                                </Ol>
                            </Section>
                            <Hr />
                        </>
                    )}
                    <Section>
                        <Heading as="h2">Cost To/ Credit To</Heading>
                        {(totalManufacturingLabor !== 0 || totalEngineeringLabor !== 0 || totalMaterialsVariance !== 0 || totalScrap !== 0 || customerBillTotal !== 0 || watertronicsBillTotal !== 0) ? (
                            <>
                                {totalManufacturingLabor !== 0 && (
                                    <Text><strong>Manufacturing Labor Total ($90/hr):</strong> ${formatCostCredit(totalManufacturingLabor)}</Text>
                                )}
                                {totalEngineeringLabor !== 0 && (
                                    <Text><strong>Engineering Labor Total ($165/hr):</strong> ${formatCostCredit(totalEngineeringLabor)}</Text>
                                )}
                                {totalMaterialsVariance !== 0 && (
                                    <Text><strong>Materials Variance Total:</strong> ${formatCostCredit(totalMaterialsVariance)}</Text>
                                )}
                                {totalScrap !== 0 && (
                                    <Text><strong>Scrap Total:</strong> ${formatCostCredit(totalScrap)}</Text>
                                )}
                                <Text><strong>{customerBillTotal > 0 ? 'Credit to Customer:' : 'Charge to Customer:'}</strong> {customerBillTotal !== 0 ? `$${Math.abs(customerBillTotal).toFixed(2)}` : 'None'}</Text>
                                {watertronicsBillTotal !== 0 && (
                                    <Text><strong>Watertronics Bill Total:</strong> ${formatCostCredit(watertronicsBillTotal)}</Text>
                                )}
                                <Hr />
                            </>
                        ) : (
                            <>
                                <Text><strong>Customer Bill Total:</strong> None</Text>
                                <Text>No other cost or credit information to display.</Text>
                            </>
                        )}
                    </Section>
                    <Hr />
                    {notes && (
                        <>
                            <Section>
                                <Heading as="h2">Notes</Heading>
                                <Text>{notes || "BLANK"}</Text>
                            </Section>
                            <Hr />
                        </>
                    )}
                    {previousChanges.length > 0 && (
                        <>
                            <Section>
                                <Heading as="h2">Previous Changes</Heading>
                                <Ol>
                                    {previousChanges.map((detail, index) => (
                                        <li key={index}>
                                            <Text>CO# {toDisplayValue(getChangeOrderNumber(detail), "BLANK")} - <strong>{getDetailTitle(detail)}</strong> changed to {getDetailNewValue(detail)}</Text>
                                        </li>
                                    ))}
                                </Ol>
                            </Section>
                            <Hr />
                        </>
                    )}
                </Html>,
                { pretty: true }
            );
        
            // Step 6: Send email
            const defaultRecipients = [
                ...localChangeOrderRecipients.map(recipient => recipient.email)
            ];
            
            const additionalRecipientsArray = additionalRecipients
                .split(";")
                .map(email => email.trim())
                .filter(email => email);
                
            const recipients = sendToUser 
                ? [userDetails.username, ...additionalRecipientsArray] 
                : [
                    ...defaultRecipients, 
                    ...additionalRecipientsArray, 
                    ...(impactsCSM ? ["NorthPrairieDL@watertronics.com"] : [])
                  ];

            console.log('Sending change order email...');
            await sendEmail({
                subject: `Change Order #${nextChangeOrderNumber} - ${projectDetails[0]?.JobNumber || "BLANK"} - ${projectDetails[0]?.ProjectName || "BLANK"}`,
                body: emailBody,
                toRecipients: recipients,
                from: "change.order@watertronics.com"
            });
            
            console.log('Change order email sent successfully');
            
            // Step 7: Refresh data to reflect changes
            await dispatch(fetchJobDetails({
                jobId,
                apiUrls: {
                    changeOrderDetails: apiUrls.changeOrderDetails,
                    changeOrderParent: apiUrls.changeOrderParent
                }
            }));
            
            console.log(`Change order #${nextChangeOrderNumber} created successfully!`);
            
        } catch (error) {
            console.error('Error creating change order:', error);
            
            // Determine user-friendly error message
            let errorMsg = "Failed to create change order. ";
            
            if (error?.message?.includes('timeout') || error?.message?.includes('network')) {
                errorMsg += "Network error - please check your connection and try again.";
            } else if (error?.response?.status === 409) {
                errorMsg += "Conflict detected - another change order may have been created simultaneously. Please refresh and try again.";
            } else if (error?.response?.status >= 500) {
                errorMsg += "Server error - please try again later or contact support.";
            } else if (error?.message?.includes('cancelled')) {
                errorMsg += "Request was cancelled due to timeout.";
            } else {
                errorMsg += `Unexpected error occurred: ${error?.message || 'Unknown error'}`;
            }
            
            setErrorMessage(errorMsg);
            
            // Don't close the modal on error so user can see the error and try again
            return;
            
        } finally {
            setIsSubmitting(false);
            setIsRefreshingData(false);
        }
        
        // Only close modal if no error occurred
        if (!errorMessage && onClose) {
            onClose();
        }
    };
    

    // Check if data is loaded
    if (!changeOrderDetails || changeOrderDetails.length === 0) {
        return <div>Loading change order details...</div>;
    }

    return (
        <Container className="change-order-document change-order-send">
            {projectDetails ? (
                <>
                    <div className="text-center">
                        <Row>
                            <Col>
                                <h1><strong>Change Order # {countChangeOrderParent() + 1}</strong></h1>
                                <h3>{projectDetails[0].JobNumber || "BLANK"} - {projectDetails[0].ProjectName || "BLANK"}</h3>
                                <h4>{projectDetails[0].ModelNumber || "BLANK"}</h4>
                                {projectDetails[0].ProjectManager === currentUser ? (
                                    <p><strong>Written By:</strong> {currentUser || "BLANK"} on {currentDate}. <strong>Sales Rep:</strong> {projectDetails[0].SalesRep || "BLANK"}</p>
                                ) : (
                                    <p><strong>Written By:</strong> {currentUser || "BLANK"} on behalf of {projectDetails[0].ProjectManager || "BLANK"} on {currentDate}. <strong>Sales Rep:</strong> {projectDetails[0].SalesRep || "BLANK"}</p>
                                )}
                                <p>
                                  {projectDetails[0].MechanicalEngineer && projectDetails[0].MechanicalEngineer !== "BLANK" && <><i className="bi bi-wrench-adjustable"/> {projectDetails[0].MechanicalEngineer}</> }
                                  {projectDetails[0].MechanicalEngineer && projectDetails[0].MechanicalEngineer !== "BLANK" && projectDetails[0].ElectricalEngineer && projectDetails[0].ElectricalEngineer !== "BLANK" && " | "}
                                  {projectDetails[0].ElectricalEngineer && projectDetails[0].ElectricalEngineer !== "BLANK" && <><i className="bi bi-plug-fill" /> {projectDetails[0].ElectricalEngineer}</> }
                                  {projectDetails[0].ElectricalEngineer && projectDetails[0].ElectricalEngineer !== "BLANK" && projectDetails[0].ControlsEngineer && projectDetails[0].ControlsEngineer !== "BLANK" && " | "}
                                  {projectDetails[0].ControlsEngineer && projectDetails[0].ControlsEngineer !== "BLANK" && <><i className="bi bi-router" /> {projectDetails[0].ControlsEngineer}</> }
                                </p>
                                <hr />
                            </Col>
                        </Row>
                    </div>
                    {hasActionItems && actionRequiredDepartments.length > 0 && (
                        <div>
                            <p><span style={{ color: 'red', fontWeight: 'bold' }}>This change order includes assigned action items for {formatDepartments(actionRequiredDepartments)}. Please review Change Order Tasks in the Production Portal for more information.</span></p>
                        </div>
                    )}
                    {groupedNoBill.length > 0 && (
                        <div>
                            <h2 className="mt-3">Changes</h2>
                            <ol>
                                {groupedNoBill.map(group => (
                                    <div key={group.reason}>
                                        <h4>{group.reason || "BLANK"}</h4>
                                        {group.details.map((detail, index) => (
                                            <li key={index}>
                                                <p className="mt-2 mb-2">
                                                    {renderChangeDescription(detail)}
                                                </p>
                                            </li>
                                        ))}
                                    </div>
                                ))}
                            </ol>
                        </div>
                    )}
                    <hr />
                    {groupedCustomerBill.length > 0 && (
                        <div>
                            <h2 className="mt-3">Changes Billed to Customer</h2>
                            <ol>
                                {groupedCustomerBill.map(group => (
                                    <div key={group.reason}>
                                        <h4>{group.reason || "BLANK"}</h4>
                                        {group.details.map((detail, index) => (
                                            <li key={index}>
                                                <p className="mt-2 mb-2">
                                                    {renderChangeDescription(detail)}
                                                </p>
                                            </li>
                                        ))}
                                    </div>
                                ))}
                            </ol>
                        </div>
                    )}
                    <hr />
                    {groupedWatertronicsBill.length > 0 && (
                        <div>
                            <h2 className="mt-3">Changes Billed to Watertronics</h2>
                            <ol>
                                {groupedWatertronicsBill.map(group => (
                                    <div key={group.reason}>
                                        <h4>{group.reason || "BLANK"}</h4>
                                        {group.details.map((detail, index) => (
                                            <li key={index}>
                                                <p className="mt-2 mb-2">
                                                    {renderChangeDescription(detail)}
                                                </p>
                                            </li>
                                        ))}
                                    </div>
                                ))}
                            </ol>
                        </div>
                    )}
                    <hr />
                    {followUp.length > 0 && (
                        <div>
                            <h2 className="mt-3">Follow Up</h2>
                            <ol>                    
                                {followUp.map((detail, index) => (
                                    <li key={index}>
                                        <p className="mt-2 mb-2">
                                            {renderChangeDescription(detail)}
                                        </p>
                                    </li>
                                ))}
                            </ol>
                        </div>
                    )}    
                    <hr />
                    {filteredTbdList.length > 0 && (
                        <div>
                            <h2 className="mt-3">TBD List</h2>
                            <div>
                                <ol>
                                    {filteredTbdList.map((tbdItem, index) => (
                                        <li key={index}>
                                            <p className="mt-2 mb-2"><strong>{tbdItem.ColumnName || "BLANK"} ({tbdItem.Table || "BLANK"}):</strong> {tbdItem.Value || "BLANK"}</p>
                                        </li>
                                    ))}
                                </ol>
                            </div>
                        </div>
                    )}
                    <hr />
                    <div>
                        <h2 className="mt-3">Cost To/ Credit To</h2>
                        {(totalManufacturingLabor !== 0 || totalEngineeringLabor !== 0 || totalMaterialsVariance !== 0 || totalScrap !== 0 || customerBillTotal !== 0 || watertronicsBillTotal !== 0) ? (
                            <>
                                {totalManufacturingLabor !== 0 && (
                                    <p><strong>Manufacturing Labor Total ($90/hr):</strong> ${formatCostCredit(totalManufacturingLabor)}</p>
                                )}
                                {totalEngineeringLabor !== 0 && (
                                    <p><strong>Engineering Labor Total ($165/hr):</strong> ${formatCostCredit(totalEngineeringLabor)}</p>
                                )}
                                {totalMaterialsVariance !== 0 && (
                                    <p><strong>Materials Variance Total:</strong> ${formatCostCredit(totalMaterialsVariance)}</p>
                                )}
                                {totalScrap !== 0 && (
                                    <p><strong>Scrap Total:</strong> ${formatCostCredit(totalScrap)}</p>
                                )}
                                <p><strong>{customerBillTotal > 0 ? 'Credit to Customer:' : 'Charge to Customer:'}</strong> {customerBillTotal !== 0 ? `$${Math.abs(customerBillTotal).toFixed(2)}` : 'None'}</p>
                                {watertronicsBillTotal !== 0 && (
                                    <p><strong>Watertronics Bill Total:</strong> ${formatCostCredit(watertronicsBillTotal)}</p>
                                )}
                                <hr />
                            </>
                        ) : (
                            <>
                                <p><strong>Charge to Customer:</strong> None</p>
                                <p>No other cost or credit information to display.</p>
                            </>
                        )}
                    </div>
                    <hr />
                    {notes && (
                        <div className="mt-3">
                            <strong>Notes:</strong>
                            <p>{notes || "BLANK"}</p>
                        </div>
                    )}
                    <hr />
                    <div className="mt-3">
                        <strong>Email Recipients:</strong>
                        {missingEngineers.length > 0 && !sendToUser && (
                            <div className="mt-2 mb-2">
                                <p style={{ color: 'red', fontWeight: 'bold' }}>
                                    Warning: The following engineers are assigned to this project but no matching recipients were found: 
                                    {missingEngineers.map((engineer, index) => (
                                        <span key={index}>
                                            {index > 0 ? ', ' : ' '}{engineer.name} ({engineer.role})
                                        </span>
                                    ))}
                                </p>
                            </div>
                        )}
                        <p>
                            {sendToUser ? (
                                `${userDetails.name}`
                            ) : (
                                <>
                                    {localChangeOrderRecipients.map((recipient, index) => (
                                        `${recipient.displayName}${index < localChangeOrderRecipients.length - 1 ? ', ' : ''}`
                                    ))}                                    {impactsCSM && localChangeOrderRecipients.length > 0 && ', '}
                                    {impactsCSM && 'NorthPrairieDL@watertronics.com'}
                                </>
                            )}
                        </p>
                        {userRoles.includes("Admin") && (
                            <Form.Check 
                                type="checkbox" 
                                label="Send to current user" 
                                checked={sendToUser} 
                                onChange={handleRecipientChange} 
                            />
                        )}
                        <Form.Group controlId="additionalRecipients" className="mt-3">
                            <Form.Label>Additional Recipients (separated by ;)</Form.Label>
                            <Form.Control 
                                type="text" 
                                value={additionalRecipients} 
                                onChange={handleAdditionalRecipientsChange} 
                                placeholder="Enter additional email recipients" 
                            />
                        </Form.Group>
                    </div>
                    <hr />
                    <div className="mt-3">
                        <strong>Previous Changes:</strong>
                        <ol>
                            {previousChanges.map((detail, index) => (
                                <li key={index}>
                                    <p>CO# {toDisplayValue(getChangeOrderNumber(detail), "BLANK")} - <strong>{getDetailTitle(detail)}</strong> changed to {getDetailNewValue(detail)}</p>
                                </li>
                            ))}
                        </ol>
                    </div>
                    <hr />
                    
                    {/* Error Message Display */}
                    {errorMessage && (
                        <Alert variant="danger" className="mt-3">
                            <Alert.Heading>Error Creating Change Order</Alert.Heading>
                            <p>{errorMessage}</p>
                            <Button 
                                variant="outline-danger" 
                                size="sm"
                                onClick={() => setErrorMessage("")}
                            >
                                Dismiss
                            </Button>
                        </Alert>
                    )}
                    
                    {/* Loading State Display */}
                    {isRefreshingData && (
                        <Alert variant="info" className="mt-3">
                            <div className="d-flex align-items-center">
                                <div className="spinner-border spinner-border-sm me-2" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                                Refreshing change order data to ensure accuracy...
                            </div>
                        </Alert>
                    )}
                    
                    <Button 
                        variant="primary" 
                        onClick={handleSubmitChangeOrder} 
                        className="mt-3" 
                        disabled={isSubmitting || isRefreshingData}
                    >
                        {isRefreshingData ? "Refreshing Data..." : 
                         isSubmitting ? "Creating Change Order..." : 
                         "Submit Change Order"}
                    </Button>
                </>
            ) : (
                <p>Loading project details...</p>
            )}
        </Container>
    );
};

export default SendChangeOrder;
